<template>
  <button
    class="device-grid-cell w-full h-full flex flex-col items-center justify-center gap-1 rounded-xl transition-colors select-none active:scale-95"
    :class="isOn ? [colorMap.bg, 'border', colorMap.border] : 'bg-white/5 border border-white/10'"
    @pointerdown="onPointerDown"
    @pointerup="onPointerUp"
    @pointerleave="onPointerCancel"
    @pointercancel="onPointerCancel"
    @contextmenu.prevent>
    <!-- Device icon — the widget's own drawing when it has one, else its glyph -->
    <span
      v-if="$slots.art"
      class="device-grid-cell__art transition-colors"
      :class="isOn ? colorMap.text : 'text-gray-400'">
      <slot name="art" />
    </span>
    <span
      v-else
      class="material-symbols-outlined transition-colors"
      :class="isOn ? colorMap.text : 'text-gray-400'"
      style="font-size: 1.75rem"
      >{{ icon }}</span
    >

    <!-- Device name -->
    <span
      class="text-[11px] font-medium leading-tight text-center line-clamp-2 px-1 transition-colors"
      :class="isOn ? 'text-white' : 'text-gray-400'"
      >{{ name }}</span
    >

    <!-- Status indicator dot -->
    <span class="w-1.5 h-1.5 rounded-full transition-colors" :class="isOn ? colorMap.dot : 'bg-gray-600'" />
  </button>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

const props = defineProps<{
  icon: string;
  name: string;
  isOn: boolean;
  activeColor?: string; // tailwind color class like 'amber-400', 'green-400', etc.
}>();

const emit = defineEmits<{
  toggle: [];
  'long-press': [];
}>();

// Static class map so Tailwind JIT can detect all class names at build time
const COLOR_MAP: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  'yellow-400': {
    bg: 'bg-yellow-400/15',
    border: 'border-yellow-400/30',
    text: 'text-yellow-400',
    dot: 'bg-yellow-400',
  },
  'green-400': { bg: 'bg-green-400/15', border: 'border-green-400/30', text: 'text-green-400', dot: 'bg-green-400' },
  'blue-400': { bg: 'bg-blue-400/15', border: 'border-blue-400/30', text: 'text-blue-400', dot: 'bg-blue-400' },
  'orange-400': {
    bg: 'bg-orange-400/15',
    border: 'border-orange-400/30',
    text: 'text-orange-400',
    dot: 'bg-orange-400',
  },
  'amber-400': { bg: 'bg-amber-400/15', border: 'border-amber-400/30', text: 'text-amber-400', dot: 'bg-amber-400' },
  'red-400': { bg: 'bg-red-400/15', border: 'border-red-400/30', text: 'text-red-400', dot: 'bg-red-400' },
  'sky-400': { bg: 'bg-sky-400/15', border: 'border-sky-400/30', text: 'text-sky-400', dot: 'bg-sky-400' },
};

const colorMap = computed(() => COLOR_MAP[props.activeColor ?? 'amber-400'] ?? COLOR_MAP['amber-400']);

const LONG_PRESS_MS = 400;
let pressTimer: ReturnType<typeof setTimeout> | null = null;
const didLongPress = ref(false);

function onPointerDown() {
  didLongPress.value = false;
  pressTimer = setTimeout(() => {
    didLongPress.value = true;
    emit('long-press');
  }, LONG_PRESS_MS);
}

function onPointerUp() {
  if (pressTimer) {
    clearTimeout(pressTimer);
    pressTimer = null;
  }
  if (!didLongPress.value) {
    emit('toggle');
  }
}

function onPointerCancel() {
  if (pressTimer) {
    clearTimeout(pressTimer);
    pressTimer = null;
  }
}
</script>

<style scoped>
.device-grid-cell {
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

/* The drawing a widget slots in instead of its glyph, in the glyph's own box. */
.device-grid-cell__art {
  display: block;
  width: 1.75rem;
  height: 1.75rem;
}
</style>
